import type { ReactNode } from 'react';

/**
 * HighlightAllahText
 *
 * Wraps sacred names and inline verse markers in plain text with real
 * React elements. Pure presentation — the underlying string is never
 * mutated and no markup is injected into the data layer.
 *
 * Matched highlights:
 *   - Allah in English, Arabic, and Urdu/Persian script
 *   - Muhammad in Urdu, including the compact salutation form محمدؐ
 *   - Bracketed or bare numeric markers, rendered as orange superscript
 *
 * Match conflicts are resolved by preferring the longest earliest token
 * so a single region of text is never double-wrapped.
 *
 * The function returns React children (string | ReactNode[]). No
 * `dangerouslySetInnerHTML` is used; spans are real React elements.
 *
 * Usage:
 *   <p><HighlightAllahText text={ayah.translation_en} /></p>
 *   <p lang="ar" dir="rtl"><HighlightAllahText text={ayah.text_ar} /></p>
 */

// Combining marks that may appear between the letters of Allah in
// Quranic Arabic text: standard Tashkeel range, dagger-alif (U+0670),
// and the Quranic recitation marks block.
const AR_DIA = '[ً-ٰٟۖ-ۭ]*';

const PATTERN_UR_ALLAH = /اللہ/g;
const PATTERN_AR = new RegExp(
  `ا${AR_DIA}ل${AR_DIA}ل${AR_DIA}ه`,
  'g',
);
const PATTERN_EN = /\bAllah\b/gi;
const PATTERN_MUHAMMAD_UR = /محمدؐ|محمد/g;
const PATTERN_AYAH_MARKER = /\[\s*([0-9۰-۹٠-٩]+)\s*\]|(?<![\p{L}\p{N}])([0-9۰-۹٠-٩]+)(?![\p{L}\p{N}])/gu;

const ALLAH_HIGHLIGHT_COLOR = '#ff5e1f';
const MUHAMMAD_HIGHLIGHT_COLOR = 'rgb(252 211 77)';
const MARKER_HIGHLIGHT_COLOR = '#f97316';

type Span = {
  start: number;
  end: number;
  kind: 'allah' | 'muhammad' | 'marker';
  text?: string;
};

function collectSpans(text: string): Span[] {
  const spans: Span[] = [];
  for (const re of [PATTERN_UR_ALLAH, PATTERN_AR, PATTERN_EN]) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      // Guard against zero-length matches to prevent infinite loops.
      if (m.index === re.lastIndex) {
        re.lastIndex++;
        continue;
      }
      spans.push({ start: m.index, end: m.index + m[0].length, kind: 'allah' });
    }
  }
  PATTERN_MUHAMMAD_UR.lastIndex = 0;
  let muhammadMatch: RegExpExecArray | null;
  while ((muhammadMatch = PATTERN_MUHAMMAD_UR.exec(text)) !== null) {
    spans.push({
      start: muhammadMatch.index,
      end: muhammadMatch.index + muhammadMatch[0].length,
      kind: 'muhammad',
    });
  }
  PATTERN_AYAH_MARKER.lastIndex = 0;
  let markerMatch: RegExpExecArray | null;
  while ((markerMatch = PATTERN_AYAH_MARKER.exec(text)) !== null) {
    const markerText = markerMatch[1] ?? markerMatch[2];
    spans.push({
      start: markerMatch.index,
      end: markerMatch.index + markerMatch[0].length,
      kind: 'marker',
      text: markerText,
    });
    if (markerMatch.index === PATTERN_AYAH_MARKER.lastIndex) {
      PATTERN_AYAH_MARKER.lastIndex++;
    }
  }
  return spans;
}

function resolveSpans(spans: Span[]): Span[] {
  if (spans.length === 0) return spans;
  const sorted = [...spans].sort(
    (a, b) => a.start - b.start || b.end - b.start - (a.end - a.start),
  );
  const out: Span[] = [];
  let cursor = 0;
  for (const span of sorted) {
    if (span.start < cursor) continue;
    out.push({ ...span });
    cursor = span.end;
  }
  return out;
}

function renderSpan(span: Span, key: number) {
  if (span.kind === 'marker') {
    return (
      <sup
        key={key}
        className="ayah-marker-sup"
        style={{ color: MARKER_HIGHLIGHT_COLOR, fontFamily: 'Arial, sans-serif' }}
      >
        {span.text}
      </sup>
    );
  }
  const color =
    span.kind === 'allah' ? ALLAH_HIGHLIGHT_COLOR : MUHAMMAD_HIGHLIGHT_COLOR;
  return (
    <span key={key} style={{ color }}>
      {span.text}
    </span>
  );
}

function spanText(text: string, span: Span) {
  if (span.kind === 'marker') return span.text;
  return text.slice(span.start, span.end);
}

function withSpanText(text: string, spans: Span[]): Span[] {
  return spans.map((span) => {
    if (span.text) return span;
    return { ...span, text: spanText(text, span) };
  });
}

function getHighlightedTextParts(text: string): Span[] {
  return withSpanText(text, resolveSpans(collectSpans(text)));
}

export function HighlightAllahText({
  text,
}: {
  text: string | null | undefined;
}): ReactNode {
  if (!text) return text ?? null;

  const resolved = getHighlightedTextParts(text);
  if (resolved.length === 0) return text;

  const out: ReactNode[] = [];
  let cursor = 0;
  resolved.forEach((span, i) => {
    if (span.start > cursor) {
      out.push(text.slice(cursor, span.start));
    }
    out.push(renderSpan(span, i));
    cursor = span.end;
  });
  if (cursor < text.length) {
    out.push(text.slice(cursor));
  }
  return out;
}
